// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║   💖 DONATAPP - CUORE ANIMATO STILE CHATGPT                               ║
// ║   File: src/components/VoiceHeart.jsx                                    ║
// ║                                                                           ║
// ║   Cuore rosso con texture "nuvole" come ChatGPT ma a forma di cuore      ║
// ║   Colori: Rosso caldo, azzurro cielo, verde tranquillo, giallo sole      ║
// ║   NO VIOLA - solo colori caldi e rassicuranti                            ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect, useRef } from 'react';

// 🎨 Palette colori per ogni voce (NO VIOLA!)
const voiceColors = {
  maria: {
    name: 'Maria',
    description: 'Dolce e affettuosa',
    primary: '#E53935',      // Rosso caldo
    secondary: '#FFCDD2',    // Rosa chiaro
    accent: '#FF8A80',       // Rosso chiaro
    gradient: 'linear-gradient(135deg, #FF8A80 0%, #E53935 50%, #C62828 100%)',
  },
  renzo: {
    name: 'Renzo',
    description: 'Calmo e rassicurante',
    primary: '#1E88E5',      // Blu cielo
    secondary: '#BBDEFB',    // Azzurro chiaro
    accent: '#64B5F6',       // Blu chiaro
    gradient: 'linear-gradient(135deg, #64B5F6 0%, #1E88E5 50%, #1565C0 100%)',
  },
  dottore: {
    name: 'Dottore',
    description: 'Professionale e gentile',
    primary: '#43A047',      // Verde tranquillo
    secondary: '#C8E6C9',    // Verde chiaro
    accent: '#81C784',       // Verde medio
    gradient: 'linear-gradient(135deg, #81C784 0%, #43A047 50%, #2E7D32 100%)',
  },
  bella: {
    name: 'Bella',
    description: 'Solare e positiva',
    primary: '#FB8C00',      // Arancione caldo
    secondary: '#FFE0B2',    // Arancione chiaro
    accent: '#FFB74D',       // Arancione medio
    gradient: 'linear-gradient(135deg, #FFB74D 0%, #FB8C00 50%, #EF6C00 100%)',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// 💖 COMPONENTE CUORE CON TEXTURE
// ═══════════════════════════════════════════════════════════════════════════

const VoiceHeart = ({
  voiceId = 'maria',           // 'maria' | 'renzo' | 'dottore' | 'bella'
  state = 'idle',              // 'idle' | 'listening' | 'thinking' | 'speaking'
  size = 280,                  // Dimensione cuore
  showName = true,             // Mostra nome sotto
  showDots = true,             // Mostra indicatori pallini
  currentDot = 0,              // Quale pallino è attivo (0-3)
  onTap = null,                // Callback tap
}) => {
  const voice = voiceColors[voiceId] || voiceColors.maria;
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [scale, setScale] = useState(1);
  
  // ─────────────────────────────────────────────────────────────────────────
  // ANIMAZIONE TEXTURE "NUVOLE" (come ChatGPT)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    let time = 0;
    
    // Funzione rumore semplificata per effetto nuvole
    const noise = (x, y, t) => {
      return Math.sin(x * 0.02 + t) * Math.cos(y * 0.02 + t * 0.7) * 
             Math.sin((x + y) * 0.01 + t * 0.5);
    };
    
    const animate = () => {
      time += state === 'speaking' ? 0.05 : state === 'thinking' ? 0.03 : 0.015;
      
      // Crea gradiente base
      const gradient = ctx.createRadialGradient(
        width * 0.4, height * 0.35, 0,
        width * 0.5, height * 0.5, width * 0.5
      );
      gradient.addColorStop(0, voice.secondary);
      gradient.addColorStop(0.5, voice.accent);
      gradient.addColorStop(1, voice.primary);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Aggiungi texture "nuvole"
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      
      for (let y = 0; y < height; y += 2) {
        for (let x = 0; x < width; x += 2) {
          const n = noise(x, y, time);
          const brightness = (n + 1) * 0.5 * 40; // -40 to +40
          
          const idx = (y * width + x) * 4;
          data[idx] = Math.min(255, Math.max(0, data[idx] + brightness));
          data[idx + 1] = Math.min(255, Math.max(0, data[idx + 1] + brightness));
          data[idx + 2] = Math.min(255, Math.max(0, data[idx + 2] + brightness));
          
          // Copia ai pixel adiacenti per performance
          if (x + 1 < width) {
            data[idx + 4] = data[idx];
            data[idx + 5] = data[idx + 1];
            data[idx + 6] = data[idx + 2];
          }
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [state, voice]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // ANIMAZIONE RESPIRO/PULSAZIONE
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let interval;
    
    const speeds = {
      idle: 3000,
      listening: 1500,
      thinking: 800,
      speaking: 600,
    };
    
    const pulseAmount = {
      idle: 0.03,
      listening: 0.05,
      thinking: 0.08,
      speaking: 0.1,
    };
    
    const pulse = () => {
      setScale(1 + pulseAmount[state]);
      setTimeout(() => setScale(1), speeds[state] / 2);
    };
    
    pulse();
    interval = setInterval(pulse, speeds[state]);
    
    return () => clearInterval(interval);
  }, [state]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 40,
      background: '#FFFFFF',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      
      {/* Titolo */}
      <h2 style={{
        fontSize: 20,
        fontWeight: 600,
        color: '#1A1A1A',
        marginBottom: 60,
      }}>
        Scegli una voce
      </h2>
      
      {/* Cuore con texture */}
      <div 
        onClick={onTap}
        style={{
          position: 'relative',
          cursor: onTap ? 'pointer' : 'default',
          transform: `scale(${scale})`,
          transition: 'transform 0.3s ease-out',
        }}
      >
        {/* Canvas per texture animata */}
        <canvas
          ref={canvasRef}
          width={size}
          height={size}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: size,
            height: size,
            opacity: 0, // Nascosto, usato solo per generare texture
          }}
        />
        
        {/* SVG Cuore con clip-path */}
        <svg 
          width={size} 
          height={size} 
          viewBox="0 0 100 100"
          style={{
            filter: `drop-shadow(0 8px 24px ${voice.primary}40)`,
          }}
        >
          <defs>
            {/* Gradiente con effetto nuvole */}
            <radialGradient id={`heartGrad-${voiceId}`} cx="35%" cy="30%">
              <stop offset="0%" stopColor={voice.secondary}>
                <animate 
                  attributeName="stop-color" 
                  values={`${voice.secondary};${voice.accent};${voice.secondary}`}
                  dur={state === 'speaking' ? '1s' : '3s'}
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="50%" stopColor={voice.accent}>
                <animate 
                  attributeName="stop-color" 
                  values={`${voice.accent};${voice.primary};${voice.accent}`}
                  dur={state === 'speaking' ? '1s' : '3s'}
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%" stopColor={voice.primary} />
            </radialGradient>
            
            {/* Filtro per effetto morbido */}
            <filter id="softGlow">
              <feGaussianBlur stdDeviation="1" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Forma cuore */}
          <path
            d="M50 88 
               C20 60, 5 40, 15 25 
               C25 10, 45 15, 50 30 
               C55 15, 75 10, 85 25 
               C95 40, 80 60, 50 88Z"
            fill={`url(#heartGrad-${voiceId})`}
            filter="url(#softGlow)"
          />
          
          {/* Riflesso lucido (effetto 3D) */}
          <ellipse
            cx="35"
            cy="32"
            rx="12"
            ry="8"
            fill="rgba(255,255,255,0.35)"
            transform="rotate(-25, 35, 32)"
          />
          
          {/* Secondo riflesso più piccolo */}
          <ellipse
            cx="65"
            cy="35"
            rx="6"
            ry="4"
            fill="rgba(255,255,255,0.2)"
            transform="rotate(-25, 65, 35)"
          />
        </svg>
      </div>
      
      {/* Nome voce */}
      {showName && (
        <div style={{ marginTop: 40, textAlign: 'center' }}>
          <h3 style={{
            fontSize: 28,
            fontWeight: 500,
            color: '#1A1A1A',
            margin: 0,
          }}>
            {voice.name}
          </h3>
          <p style={{
            fontSize: 16,
            color: '#666666',
            marginTop: 8,
          }}>
            {voice.description}
          </p>
        </div>
      )}
      
      {/* Indicatori pallini (per swipe) */}
      {showDots && (
        <div style={{
          display: 'flex',
          gap: 8,
          marginTop: 32,
        }}>
          {Object.keys(voiceColors).map((v, i) => (
            <div
              key={v}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: voiceId === v ? '#1A1A1A' : '#D0D0D0',
                transition: 'background 0.3s',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎮 DEMO CON SWIPE
// ═══════════════════════════════════════════════════════════════════════════

export const VoiceHeartDemo = () => {
  const [currentVoice, setCurrentVoice] = useState('maria');
  const [state, setState] = useState('idle');
  const voices = ['maria', 'renzo', 'dottore', 'bella'];
  
  const nextVoice = () => {
    const idx = voices.indexOf(currentVoice);
    setCurrentVoice(voices[(idx + 1) % voices.length]);
  };
  
  const prevVoice = () => {
    const idx = voices.indexOf(currentVoice);
    setCurrentVoice(voices[(idx - 1 + voices.length) % voices.length]);
  };
  
  // Simula conversazione
  const startConversation = () => {
    setState('listening');
    setTimeout(() => setState('thinking'), 2000);
    setTimeout(() => setState('speaking'), 3500);
    setTimeout(() => setState('idle'), 7000);
  };
  
  return (
    <div style={{ position: 'relative' }}>
      <VoiceHeart
        voiceId={currentVoice}
        state={state}
        onTap={startConversation}
      />
      
      {/* Frecce navigazione */}
      <button
        onClick={prevVoice}
        style={{
          position: 'fixed',
          left: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 50,
          height: 50,
          borderRadius: '50%',
          border: 'none',
          background: '#F5F5F5',
          fontSize: 24,
          cursor: 'pointer',
        }}
      >
        ←
      </button>
      <button
        onClick={nextVoice}
        style={{
          position: 'fixed',
          right: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 50,
          height: 50,
          borderRadius: '50%',
          border: 'none',
          background: '#F5F5F5',
          fontSize: 24,
          cursor: 'pointer',
        }}
      >
        →
      </button>
      
      {/* Controlli stato (per test) */}
      <div style={{
        position: 'fixed',
        bottom: 20,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
      }}>
        {[
          { id: 'idle', label: 'In attesa' },
          { id: 'listening', label: 'Ascolto' },
          { id: 'thinking', label: 'Penso' },
          { id: 'speaking', label: 'Parlo' }
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => setState(item.id)}
            style={{
              padding: '8px 16px',
              borderRadius: 20,
              border: state === item.id ? '2px solid #1A1A1A' : '1px solid #E0E0E0',
              background: state === item.id ? '#F5F5F5' : '#FFFFFF',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default VoiceHeart;
